import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { User, Settings, FileText, Loader2, Building2, CreditCard } from 'lucide-react'
import { toast } from 'sonner'
import { apiFetch } from '@/lib/apiFetch'
import type { AuthUser } from '@/lib/auth'

interface SettingsModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  user: AuthUser | null
  onAccountDeleted: () => void
  onOpenInvite: () => void
}

type SettingsTab = 'account' | 'organization' | 'preferences' | 'defaults'

const tabs: { id: SettingsTab; label: string; icon: typeof User }[] = [
  { id: 'account', label: 'Account', icon: User },
  { id: 'organization', label: 'Organization', icon: Building2 },
  { id: 'preferences', label: 'Preferences', icon: Settings },
  { id: 'defaults', label: 'Document Defaults', icon: FileText },
]

interface OrgInfo {
  id: string
  name: string
  role: string
}

interface OrgMember {
  user_id: string
  email: string
  first_name: string | null
  last_name: string | null
  role: string
  joined_at: string
}

function memberDisplayName(m: OrgMember): string {
  const first = (m.first_name ?? '').trim()
  const last = (m.last_name ?? '').trim()
  if (first || last) return `${first} ${last}`.trim()
  return m.email.split('@')[0]
}

export function SettingsModal({ open, onOpenChange, user, onAccountDeleted, onOpenInvite }: SettingsModalProps) {
  const [activeTab, setActiveTab] = useState<SettingsTab>('account')
  const [citationStyle, setCitationStyle] = useState('ama')
  const [isResetting, setIsResetting] = useState(false)
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false)
  const [deleteConfirmText, setDeleteConfirmText] = useState('')
  const [isDeleting, setIsDeleting] = useState(false)

  const [org, setOrg] = useState<OrgInfo | null>(null)
  const [members, setMembers] = useState<OrgMember[]>([])
  const [isLoadingOrg, setIsLoadingOrg] = useState(false)
  const [nameDraft, setNameDraft] = useState('')
  const [isEditingName, setIsEditingName] = useState(false)
  const [isSavingName, setIsSavingName] = useState(false)
  const [isOpeningPortal, setIsOpeningPortal] = useState(false)

  useEffect(() => {
    if (!open) return
    let cancelled = false
    /* eslint-disable react-hooks/set-state-in-effect -- modal-open side effect: fetch org+members when the dialog opens, cancel on close */
    setIsLoadingOrg(true)
    Promise.all([
      apiFetch('/api/org').then((r) => (r.ok ? r.json() : null)),
      apiFetch('/api/org/members').then((r) => (r.ok ? r.json() : [])),
    ])
      .then(([o, ms]) => {
        if (cancelled) return
        if (o) {
          setOrg(o as OrgInfo)
          setNameDraft((o as OrgInfo).name)
        }
        setMembers((ms as OrgMember[]) ?? [])
      })
      .catch(() => {
        if (!cancelled) toast.error('Failed to load organization.')
      })
      .finally(() => {
        if (!cancelled) setIsLoadingOrg(false)
      })
    /* eslint-enable react-hooks/set-state-in-effect */
    return () => {
      cancelled = true
    }
  }, [open])

  const handleSaveOrgName = async () => {
    const trimmed = nameDraft.trim()
    if (!trimmed || trimmed === org?.name) {
      setIsEditingName(false)
      setNameDraft(org?.name ?? '')
      return
    }
    setIsSavingName(true)
    try {
      const r = await apiFetch('/api/org', {
        method: 'PATCH',
        body: JSON.stringify({ name: trimmed }),
      })
      if (!r.ok) {
        const body = (await r.json().catch(() => ({}))) as { detail?: string }
        throw new Error(body.detail ?? `HTTP ${r.status}`)
      }
      const updated = (await r.json()) as OrgInfo
      setOrg(updated)
      setNameDraft(updated.name)
      setIsEditingName(false)
      toast.success('Organization renamed.')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to rename organization.')
    } finally {
      setIsSavingName(false)
    }
  }

  const handleManageBilling = async () => {
    setIsOpeningPortal(true)
    try {
      const r = await apiFetch('/api/billing/portal', { method: 'POST' })
      if (!r.ok) {
        const body = (await r.json().catch(() => ({}))) as { detail?: string }
        // 404 = no Stripe customer yet (e.g. manual/enterprise org or not provisioned).
        throw new Error(body.detail ?? `HTTP ${r.status}`)
      }
      const { url } = (await r.json()) as { url: string }
      // Same-tab redirect: Stripe's portal returns here (return_url = /settings).
      window.location.href = url
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not open billing portal.')
      setIsOpeningPortal(false)
    }
  }

  const handlePasswordReset = async () => {
    setIsResetting(true)
    try {
      const r = await apiFetch('/auth/password-reset', { method: 'POST' })
      if (!r.ok) {
        const body = (await r.json().catch(() => ({}))) as { detail?: string }
        throw new Error(body.detail ?? `HTTP ${r.status}`)
      }
      const { url } = (await r.json()) as { url: string }
      window.open(url, '_blank', 'noopener,noreferrer')
      toast.success('Password reset link opened in a new tab.')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not start password reset.')
    } finally {
      setIsResetting(false)
    }
  }

  const handleDeleteAccount = async () => {
    setIsDeleting(true)
    try {
      const r = await apiFetch('/api/account', { method: 'DELETE' })
      if (!r.ok) {
        const body = (await r.json().catch(() => ({}))) as { detail?: string }
        throw new Error(body.detail ?? `HTTP ${r.status}`)
      }
      toast.success('Account deleted.')
      setConfirmDeleteOpen(false)
      onOpenChange(false)
      onAccountDeleted()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete account.')
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] p-0 gap-0">
        <DialogHeader className="px-6 pt-6 pb-4">
          <DialogTitle>Settings</DialogTitle>
        </DialogHeader>

        <div className="flex min-h-[360px]">
          <nav className="w-44 shrink-0 border-r border-border p-2 flex flex-col gap-0.5">
            {tabs.map((tab) => {
              const Icon = tab.icon
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 rounded-md px-3 py-2 text-sm text-left w-full transition-colors ${
                    activeTab === tab.id
                      ? 'bg-accent text-foreground font-medium'
                      : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {tab.label}
                </button>
              )
            })}
          </nav>

          <div className="flex-1 p-6">
            {activeTab === 'account' && (
              <div className="flex flex-col gap-5">
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-medium text-foreground">Email</label>
                  <div className="h-9 rounded-md border border-border bg-muted/30 px-3 text-sm flex items-center text-muted-foreground">
                    {user?.email ?? '—'}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Contact support to change it.
                  </p>
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-medium text-foreground">Password</label>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-fit"
                    onClick={handlePasswordReset}
                    disabled={isResetting}
                  >
                    {isResetting ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" /> : null}
                    Manage password
                  </Button>
                  <p className="text-xs text-muted-foreground">
                    Add or change a password as a sign-in method.
                  </p>
                </div>
                <div className="border-t border-border pt-4 mt-2">
                  <h3 className="text-sm font-medium text-red-600 mb-2">Danger Zone</h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={() => {
                      setDeleteConfirmText('')
                      setConfirmDeleteOpen(true)
                    }}
                  >
                    Delete Account
                  </Button>
                </div>
              </div>
            )}

            {activeTab === 'organization' && (
              <div className="flex flex-col gap-5">
                {isLoadingOrg && !org ? (
                  <div className="flex items-center justify-center py-10">
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                  </div>
                ) : !org ? (
                  <p className="text-sm text-muted-foreground">No organization found.</p>
                ) : (
                  <>
                    <div className="flex flex-col gap-1.5">
                      <label className="text-sm font-medium text-foreground">Organization name</label>
                      {org.role === 'admin' && isEditingName ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={nameDraft}
                            onChange={(e) => setNameDraft(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleSaveOrgName()
                              if (e.key === 'Escape') {
                                setIsEditingName(false)
                                setNameDraft(org.name)
                              }
                            }}
                            disabled={isSavingName}
                            autoFocus
                            className="flex-1 h-9 rounded-md border border-border bg-background px-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                          />
                          <Button
                            size="sm"
                            onClick={handleSaveOrgName}
                            disabled={isSavingName || !nameDraft.trim()}
                            className="bg-pharma-primary hover:bg-pharma-primary/90 text-white"
                          >
                            {isSavingName ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Save'}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setIsEditingName(false)
                              setNameDraft(org.name)
                            }}
                            disabled={isSavingName}
                          >
                            Cancel
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-9 rounded-md border border-border bg-muted/30 px-3 text-sm flex items-center text-foreground">
                            {org.name}
                          </div>
                          {org.role === 'admin' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setIsEditingName(true)}
                            >
                              Rename
                            </Button>
                          )}
                        </div>
                      )}
                      {org.role !== 'admin' && (
                        <p className="text-xs text-muted-foreground">
                          Only admins can rename the organization.
                        </p>
                      )}
                    </div>

                    <div className="flex flex-col gap-2">
                      <div className="flex items-center justify-between">
                        <label className="text-sm font-medium text-foreground">
                          Members ({members.length})
                        </label>
                        {org.role === 'admin' && (
                          <Button size="sm" variant="outline" onClick={onOpenInvite}>
                            Invite members
                          </Button>
                        )}
                      </div>
                      <div className="rounded-md border border-border divide-y divide-border">
                        {members.length === 0 ? (
                          <div className="px-3 py-2 text-sm text-muted-foreground">No members.</div>
                        ) : (
                          members.map((m) => {
                            const isYou = m.user_id === user?.id
                            return (
                              <div
                                key={m.user_id}
                                className="flex items-center justify-between px-3 py-2 text-sm"
                              >
                                <div className="flex flex-col min-w-0">
                                  <span className="text-foreground truncate">
                                    {memberDisplayName(m)}
                                    {isYou && (
                                      <span className="ml-1.5 text-xs text-muted-foreground">(you)</span>
                                    )}
                                  </span>
                                  <span className="text-xs text-muted-foreground truncate">
                                    {m.email}
                                  </span>
                                </div>
                                <span className="text-xs text-muted-foreground capitalize ml-2 shrink-0">
                                  {m.role}
                                </span>
                              </div>
                            )
                          })
                        )}
                      </div>
                    </div>

                    {org.role === 'admin' && (
                      <div className="flex flex-col gap-2 border-t border-border pt-4">
                        <label className="text-sm font-medium text-foreground">Billing</label>
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-fit"
                          onClick={handleManageBilling}
                          disabled={isOpeningPortal}
                        >
                          {isOpeningPortal ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" />
                          ) : (
                            <CreditCard className="h-3.5 w-3.5 mr-2" />
                          )}
                          Manage billing
                        </Button>
                        <p className="text-xs text-muted-foreground">
                          Opens Stripe to update your card, download invoices, or cancel.
                        </p>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {activeTab === 'preferences' && (
              <div className="flex flex-col gap-5">
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-medium text-foreground">Theme</label>
                  <Select defaultValue="light">
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="light">Light</SelectItem>
                      <SelectItem value="dark">Dark</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-medium text-foreground">Language</label>
                  <Select defaultValue="en">
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="en">English</SelectItem>
                      <SelectItem value="fr">French</SelectItem>
                      <SelectItem value="de">German</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {activeTab === 'defaults' && (
              <div className="flex flex-col gap-5">
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-medium text-foreground">Default Citation Style</label>
                  <Select value={citationStyle} onValueChange={setCitationStyle}>
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ama">AMA</SelectItem>
                      <SelectItem value="apa">APA</SelectItem>
                      <SelectItem value="vancouver">Vancouver</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    This sets the default citation format for new documents.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </DialogContent>

      <Dialog open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
        <DialogContent className="sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle>Delete account</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-3 text-sm">
            <p>
              This permanently deletes your account, your personal organization, all
              workspaces, documents, sources, and audit history. <strong>This cannot be undone.</strong>
            </p>
            <p className="text-muted-foreground">
              Type <code className="px-1 py-0.5 bg-muted rounded text-foreground">DELETE</code> to confirm.
            </p>
            <input
              type="text"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder="DELETE"
              className="h-9 rounded-md border border-border bg-background px-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmDeleteOpen(false)} disabled={isDeleting}>
              Cancel
            </Button>
            <Button
              onClick={handleDeleteAccount}
              disabled={deleteConfirmText !== 'DELETE' || isDeleting}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {isDeleting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Delete account
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  )
}
